import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { List, LayoutGrid } from "lucide-react";
import { LedgerView } from "./overview/LedgerView";
import { ScorecardView } from "./overview/ScorecardView";
import { MANROPE } from "./overview/overviewTokens";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VIEW_KEY = "equipr-overview-view";

const PERIODS = [
  { id: "this_week", label: "This week" },
  { id: "month", label: new Date().toLocaleString("en-AU", { month: "long" }) },
  { id: "quarter", label: "Quarter" },
];

const Segmented = ({ options, value, onChange, accent, testid }) => (
  <div className="inline-flex items-center gap-0 bg-white border border-[#DAD5CA] rounded-[8px] p-[3px]" data-testid={testid}>
    {options.map((o) => {
      const active = o.id === value;
      return (
        <button key={o.id} onClick={() => onChange(o.id)} data-testid={`${testid}-${o.id}`}
                className={`inline-flex items-center gap-1.5 px-3 h-[28px] rounded-[6px] text-[12.5px] transition-colors duration-150 ${active ? "font-bold" : "font-semibold text-[#59554D] hover:text-[#1A1A1A]"}`}
                style={active ? { background: accent ? "#FFD108" : "#121212", color: accent ? "#121212" : "#FFFFFF" } : undefined}>
          {o.icon && <o.icon className="w-[13px] h-[13px]" />}
          {o.label}
        </button>
      );
    })}
  </div>
);

const Skeleton = () => (
  <div className="space-y-4" data-testid="overview-skeleton">
    <div className="rounded-[10px] bg-[#121212]" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))" }}>
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="px-5 py-4" style={{ borderLeft: i ? "1px solid #2A2A2A" : "none" }}>
          <div className="h-[10px] w-16 bg-[#2A2A2A] rounded animate-pulse" />
          <div className="mt-2 h-[38px] w-24 bg-[#2A2A2A] rounded animate-pulse" />
          <div className="mt-2 h-[12px] w-28 bg-[#2A2A2A] rounded animate-pulse" />
        </div>
      ))}
    </div>
    <div className="bg-white border border-[#DAD5CA] rounded-[10px] overflow-hidden">
      <div className="h-[36px] bg-[#FBFAF7]" />
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="px-5 py-4 border-t border-[#EFECE4] flex items-center gap-6">
          <div className="h-[34px] w-[190px] bg-[#F1EEE6] rounded animate-pulse" />
          <div className="h-[34px] flex-1 bg-[#F1EEE6] rounded animate-pulse" />
          <div className="h-[34px] w-[240px] bg-[#F1EEE6] rounded animate-pulse" />
        </div>
      ))}
    </div>
  </div>
);

export default function CompanyOverview({ onNavigate }) {
  const [view, setView] = useState(() => (localStorage.getItem(VIEW_KEY) === "score" ? "score" : "ledger"));
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await axios.get(`${API}/company/overview`, { params: { period } });
      setData(r.data);
    } catch (e) {
      setError("Couldn't load the company overview.");
    }
  }, [period]);

  useEffect(() => { setData(null); load(); }, [load]);

  const chooseView = (v) => { setView(v); localStorage.setItem(VIEW_KEY, v); };
  const open = (a) => { if (a?.link && onNavigate) onNavigate(a.link); };

  return (
    <div className="space-y-5" data-testid="company-overview">
      <div className="flex flex-wrap items-start gap-3">
        <div style={{ flex: "1 1 420px", minWidth: 0 }}>
          <h1 className="text-[25px] font-bold text-[#1A1A1A] tracking-[-0.018em]" style={MANROPE} data-testid="overview-title">Overview</h1>
          <p className="mt-1 text-[14px] text-[#59554D]">How the business is running across revenue, sales, customers, support and marketing.</p>
        </div>
        <Segmented testid="overview-view-switch" value={view} onChange={chooseView}
                   options={[{ id: "ledger", label: "Ledger", icon: List }, { id: "score", label: "Scorecard", icon: LayoutGrid }]} />
        <Segmented testid="overview-period-switch" value={period} onChange={setPeriod} options={PERIODS} accent />
      </div>

      {error ? (
        <div className="bg-white border border-[#DAD5CA] rounded-[10px] p-6" data-testid="overview-error">
          <div className="text-[14px] text-[#1A1A1A]">{error}</div>
          <button onClick={load} data-testid="overview-retry"
                  className="mt-2 text-[12.5px] font-semibold text-[#8A6D00] hover:text-[#6B5600] hover:underline">Try again</button>
        </div>
      ) : !data ? <Skeleton />
        : view === "ledger" ? <LedgerView data={data} onOpen={open} />
          : <ScorecardView data={data} onOpen={open} />}
    </div>
  );
}
